/**
 * Price-chart geometry (F12, plan §1.6 · §3.3).
 *
 * A component receives coordinates and never computes them, so the line/area
 * paths, the grid and the label anchors are built here — pure, no DOM,
 * property-testable — rather than in the React tree that draws them.
 *
 * Port of the demo's `path(arr, w, h, pad)`, with one deliberate divergence: a
 * zero-span series is centred rather than scaled. Scaling it normally pins every
 * point to y = h - pad, which is exactly the area path's closing edge, so a flat
 * (or single-bar) series drew a line indistinguishable from the baseline.
 */
import {
  CHART_GRID_LINES,
  CHART_HEIGHT,
  CHART_LABEL_HIGH_Y,
  CHART_LABEL_INSET_X,
  CHART_LABEL_LOW_Y_OFFSET,
  CHART_MAX_POINTS,
  CHART_PAD,
  CHART_PRECISION,
  CHART_WIDTH,
} from '../constants/chart';

export interface ChartGridLine {
  readonly key: string;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** Label anchor points, so the component reads coordinates rather than deriving them. */
export interface ChartLabelAnchors {
  readonly leftX: number;
  readonly rightX: number;
  readonly highY: number;
  readonly lowY: number;
}

export interface ChartGeometry {
  readonly viewBox: string;
  readonly line: string;
  readonly area: string;
  readonly high: number;
  readonly low: number;
  readonly pointCount: number;
  readonly grid: readonly ChartGridLine[];
  readonly bottomY: number;
  readonly labels: ChartLabelAnchors;
}

interface Range {
  readonly lo: number;
  readonly hi: number;
}

/**
 * `first` is passed in rather than re-read: the caller has already destructured
 * it to prove the array is non-empty, so re-indexing here would reintroduce an
 * `undefined` branch that local control flow can never take (style §4 — state
 * the invariant, do not guard it).
 */
function endpointsOnly(bars: readonly number[], first: number): readonly number[] {
  const last = bars.reduce((_unused, v) => v, first);
  return first === last ? [first] : [first, last];
}

function sampleEvenly(bars: readonly number[], maxPoints: number): readonly number[] {
  const lastIdx = bars.length - 1;
  return Array.from({ length: maxPoints }, (_unused, i) =>
    i === maxPoints - 1 ? lastIdx : Math.round((i / (maxPoints - 1)) * lastIdx),
  )
    .map((idx) => bars[idx])
    .filter((v): v is number => v !== undefined);
}

/**
 * Evenly sample while preserving endpoints. Pass-through when under the max.
 * `maxPoints <= 0` means no decimation; `maxPoints === 1` keeps first+last.
 */
export function decimateBars(bars: readonly number[], maxPoints: number): readonly number[] {
  /* Destructured, not `.length === 0`: this narrows `first` for endpointsOnly,
     and the empty case is exercised by the decimateBars([], 10) test. */
  const [first] = bars;
  if (first === undefined) return bars;
  if (maxPoints <= 0 || bars.length <= maxPoints) return bars;
  if (maxPoints === 1) return endpointsOnly(bars, first);
  return sampleEvenly(bars, maxPoints);
}

/** Total over a non-empty series: the caller supplies the seed it already holds. */
function minMax(bars: readonly number[], seed: number): Range {
  return bars.reduce<Range>(
    (acc, v) => ({ lo: v < acc.lo ? v : acc.lo, hi: v > acc.hi ? v : acc.hi }),
    { lo: seed, hi: seed },
  );
}

function pathD(bars: readonly number[], range: Range): string {
  const rawSpan = range.hi - range.lo;
  const isFlat = rawSpan === 0;
  const span = isFlat ? 1 : rawSpan;
  const plotH = CHART_HEIGHT - CHART_PAD * 2;
  const midY = CHART_PAD + plotH / 2;
  const n = bars.length;
  const parts = bars.map((v, i) => {
    const x = n === 1 ? 0 : (i / (n - 1)) * CHART_WIDTH;
    const y = isFlat ? midY : CHART_PAD + (1 - (v - range.lo) / span) * plotH;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(CHART_PRECISION)} ${y.toFixed(CHART_PRECISION)}`;
  });
  /* A lone moveto renders nothing: widen a single bar into a drawable segment. */
  const tail =
    parts.length === 1
      ? [`L${CHART_WIDTH.toFixed(CHART_PRECISION)} ${midY.toFixed(CHART_PRECISION)}`]
      : [];
  return [...parts, ...tail].join('');
}

/** Fixed grid — constant for CHART_HEIGHT / CHART_PAD; shared across geometry builds. */
const CHART_GRID: readonly ChartGridLine[] = Array.from(
  { length: CHART_GRID_LINES },
  (_unused, i) => {
    const y = CHART_PAD + i * ((CHART_HEIGHT - CHART_PAD * 2) / (CHART_GRID_LINES - 1));
    return { key: `g-${String(i)}`, x1: 0, y1: y, x2: CHART_WIDTH, y2: y };
  },
);

const CHART_LABEL_ANCHORS: ChartLabelAnchors = {
  leftX: CHART_LABEL_INSET_X,
  rightX: CHART_WIDTH - CHART_LABEL_INSET_X,
  highY: CHART_LABEL_HIGH_Y,
  lowY: CHART_HEIGHT - CHART_LABEL_LOW_Y_OFFSET,
};

/** `viewBox` attribute for the SVG that draws a `buildChartGeometry` result. */
export const CHART_VIEWBOX = `0 0 ${String(CHART_WIDTH)} ${String(CHART_HEIGHT)}`;

/**
 * Build chart paths from a complete series. High/low (and the y-scale) use the
 * full finite series; decimation only thins path vertices for long windows, so
 * a spike that even sampling skips still sets the labelled high.
 *
 * Returns `null` for an empty series or one containing a non-finite bar — a
 * missing price is not a price of zero (R2b).
 */
export function buildChartGeometry(
  bars: readonly number[],
  maxPoints: number = CHART_MAX_POINTS,
): ChartGeometry | null {
  /* Destructuring proves non-emptiness to the type system, so nothing below
     needs an `undefined` guard that local control flow could never take. */
  const [first] = bars;
  if (first === undefined) return null;
  if (!bars.every((b) => Number.isFinite(b))) return null;

  const range = minMax(bars, first);
  const sampled = decimateBars(bars, maxPoints);
  const line = pathD(sampled, range);
  const bottomY = CHART_HEIGHT - CHART_PAD;
  const area = `${line}L${String(CHART_WIDTH)} ${String(bottomY)}L0 ${String(bottomY)}Z`;

  return {
    viewBox: CHART_VIEWBOX,
    line,
    area,
    high: range.hi,
    low: range.lo,
    /* The FULL series, not the decimated one: the bar count the user reads must
       describe the data, not how many vertices survived thinning. */
    pointCount: bars.length,
    grid: CHART_GRID,
    bottomY,
    labels: CHART_LABEL_ANCHORS,
  };
}
